import { useSortable } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { Group, useMantineTheme } from "@mantine/core"
import { IconEye, IconGripVertical, IconPinFilled } from "@tabler/icons-react"
import { Column } from "@tanstack/react-table"

import { ActionIcon } from "../../../atoms/ActionIcons/ActionIcon"
import { Label } from "../../../atoms/Label/Label"
import { IconVisible } from "../../IconVisibile/IconVisible"
import { getColumnLabel } from "../utils"

type ColumnDraggableItemProps<T> = {
  column: Column<T, unknown>
  isDragging?: boolean
  pinned?: boolean
}

export const ColumnDraggableItem = <T,>({
  column,
  isDragging = false,
  pinned = false,
}: ColumnDraggableItemProps<T>) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isSortableDragging,
  } = useSortable({
    id: column.id,
    disabled: pinned, // Disable dragging for pinned columns
  })

  const style = pinned
    ? {}
    : {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isSortableDragging ? 0.4 : 1,
        cursor: isSortableDragging ? "grabbing" : "default",
        border: "none",
      }

  const theme = useMantineTheme()

  const handleVisibilityToggle = () => {
    if (pinned) {
      column.pin(false)
    }
    column.toggleVisibility(!column.getIsVisible())
  }

  return (
    <Group
      ref={setNodeRef}
      style={style}
      px={0}
      py={2}
      h="fit-content"
      w="100%"
      justify="space-between"
      bg={isDragging ? "var(--mantine-color-gray-0)" : "white"}
    >
      <Group
        gap="xs"
        wrap="nowrap"
        w="100%"
        maw="100%"
        miw="100%"
        justify="space-between"
      >
        <Group wrap="nowrap" maw="85%">
          {pinned ? (
            <IconPinFilled
              size={theme.other.icon.size}
              stroke={theme.other.icon.stroke}
              color="var(--mantine-color-dimmed)"
            />
          ) : (
            <div
              {...attributes}
              {...listeners}
              style={{
                cursor: "grab",
                touchAction: "none",
                display: "flex",
                alignItems: "center",
                minWidth: theme.other.icon.size,
              }}
            >
              <IconGripVertical
                size={theme.other.icon.size}
                stroke={theme.other.icon.stroke}
              />
            </div>
          )}
          <Label label={getColumnLabel(column)} />
        </Group>
        <Group wrap="nowrap" maw="10%">
          <ActionIcon
            onClick={handleVisibilityToggle}
            variant="tertiary"
            icon={
              pinned ? (
                <IconEye />
              ) : (
                <IconVisible isVisible={column.getIsVisible()} />
              )
            }
          />
        </Group>
      </Group>
    </Group>
  )
}
